/**
 * Rejecting a quotation — WHY (Akhil, 2026-09-21). Several lost reasons and several competitors
 * may apply, either picked from the masters or created here; a free-text note carries anything
 * else. All of it travels on `decide_quotation` and is synced to G6. Winning needs no dialog.
 */
import { useState } from 'react'
import { Modal } from '../../components/Modal'
import { Button } from '../../components/ui'
import { useToast } from '../../components/Toast'
import { useCompetitors, useCreateCompetitor, useCreateLostReason, useLostReasons } from '../../lib/api/useLostReasons'
import type { LostDetails } from '../../lib/api/useQuotation'

export function RejectQuotationModal({
  quotationId,
  enquiryId,
  busy,
  onReject,
  onClose,
}: {
  quotationId: string
  enquiryId: string
  busy: boolean
  onReject: (lost: LostDetails) => void
  onClose: () => void
}) {
  const toast = useToast()
  const reasons = useLostReasons()
  const competitors = useCompetitors()
  const [pickedReasons, setPickedReasons] = useState<Set<string>>(new Set())
  const [pickedCompetitors, setPickedCompetitors] = useState<Set<string>>(new Set())
  const [note, setNote] = useState('')
  const [newReason, setNewReason] = useState('')
  const [newCompetitor, setNewCompetitor] = useState('')
  const [newWebsite, setNewWebsite] = useState('')

  const createReason = useCreateLostReason({
    onSuccess: (r) => {
      setPickedReasons((s) => new Set(s).add(r.name))
      setNewReason('')
    },
    onError: (e) => toast(`Could not add the reason: ${e.message}`),
  })
  const createCompetitor = useCreateCompetitor({
    onSuccess: (c) => {
      setPickedCompetitors((s) => new Set(s).add(c.name))
      setNewCompetitor('')
      setNewWebsite('')
    },
    onError: (e) => toast(`Could not add the competitor: ${e.message}`),
  })

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    set((s) => {
      const next = new Set(s)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const canReject = pickedReasons.size > 0 || note.trim() !== ''
  const [reasonSearch, setReasonSearch] = useState('')
  const [competitorSearch, setCompetitorSearch] = useState('')
  const shownReasons = (reasons.data ?? []).filter((r) => r.label.toLowerCase().includes(reasonSearch.trim().toLowerCase()))
  const shownCompetitors = (competitors.data ?? []).filter((c) => `${c.label} ${c.website}`.toLowerCase().includes(competitorSearch.trim().toLowerCase()))

  return (
    <Modal
      title={`Reject ${quotationId}`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            {enquiryId} moves to Rejected; the reasons and competitors are synced to the ERP.
          </span>
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={busy || !canReject}
            title={canReject ? undefined : 'Pick at least one reason, or say why in the note'}
            onClick={() => onReject({ lost_reasons: [...pickedReasons], competitors: [...pickedCompetitors], order_lost_reason: note })}
          >
            {busy ? 'Rejecting…' : 'Reject quotation'}
          </Button>
        </>
      }
    >
      <div className="pad" style={{ display: 'grid', gap: 14 }}>
        <div>
          <b style={{ fontSize: 13 }}>Why was it lost?</b>
          <div style={{ color: 'var(--muted)', fontSize: 12, margin: '2px 0 8px' }}>Tick every reason that applies.</div>
          {/* A scrolling checklist, not chips (Akhil, 2026-09-21): the masters grow, and a long
              list needs a search box and a fixed height more than it needs to look compact. */}
          <input value={reasonSearch} placeholder={`Search ${reasons.data?.length ?? ''} reasons…`} style={{ width: '100%', marginBottom: 6 }} onChange={(e) => setReasonSearch(e.target.value)} />
          <div className="picklist">
            {shownReasons.map((r) => (
              <label key={r.name} className={`pick${pickedReasons.has(r.name) ? ' on' : ''}`}>
                <input type="checkbox" checked={pickedReasons.has(r.name)} onChange={() => toggle(setPickedReasons, r.name)} />
                <span>{r.label}</span>
              </label>
            ))}
            {reasons.isPending ? <div className="pick muted">Loading reasons…</div> : null}
            {!reasons.isPending && shownReasons.length === 0 ? <div className="pick muted">{reasonSearch ? 'No reason matches — add it below.' : 'No reasons yet — add one below.'}</div> : null}
          </div>
          {pickedReasons.size > 0 ? <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{pickedReasons.size} selected</div> : null}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input value={newReason} placeholder="Add a reason not listed…" style={{ flex: 1 }} onChange={(e) => setNewReason(e.target.value)} />
            <Button size="sm" disabled={createReason.isPending || newReason.trim() === ''} onClick={() => createReason.mutate(newReason)}>
              {createReason.isPending ? 'Adding…' : '+ Add reason'}
            </Button>
          </div>
        </div>

        <div>
          <b style={{ fontSize: 13 }}>Lost to</b>
          <div style={{ color: 'var(--muted)', fontSize: 12, margin: '2px 0 8px' }}>The competitor(s) that won it, if known.</div>
          <input value={competitorSearch} placeholder={`Search ${competitors.data?.length ?? ''} competitors…`} style={{ width: '100%', marginBottom: 6 }} onChange={(e) => setCompetitorSearch(e.target.value)} />
          <div className="picklist">
            {shownCompetitors.map((c) => (
              <label key={c.name} className={`pick${pickedCompetitors.has(c.name) ? ' on' : ''}`}>
                <input type="checkbox" checked={pickedCompetitors.has(c.name)} onChange={() => toggle(setPickedCompetitors, c.name)} />
                <span>
                  {c.label}
                  {c.website ? <span style={{ color: 'var(--muted)', fontSize: 11.5 }}> · {c.website}</span> : null}
                </span>
              </label>
            ))}
            {competitors.isPending ? <div className="pick muted">Loading competitors…</div> : null}
            {!competitors.isPending && shownCompetitors.length === 0 ? <div className="pick muted">{competitorSearch ? 'No competitor matches — add it below.' : 'No competitors yet — add one below.'}</div> : null}
          </div>
          {pickedCompetitors.size > 0 ? <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{pickedCompetitors.size} selected</div> : null}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <input value={newCompetitor} placeholder="Competitor name…" style={{ flex: '1 1 160px' }} onChange={(e) => setNewCompetitor(e.target.value)} />
            <input value={newWebsite} placeholder="Website (optional)" style={{ flex: '1 1 160px' }} onChange={(e) => setNewWebsite(e.target.value)} />
            <Button size="sm" disabled={createCompetitor.isPending || newCompetitor.trim() === ''} onClick={() => createCompetitor.mutate({ name: newCompetitor, website: newWebsite })}>
              {createCompetitor.isPending ? 'Adding…' : '+ Add competitor'}
            </Button>
          </div>
        </div>

        <label className="ef">
          <span>Anything else</span>
          <textarea value={note} rows={3} placeholder="What the customer said, the price they got elsewhere…" onChange={(e) => setNote(e.target.value)} style={{ width: '100%', resize: 'vertical', font: 'inherit' }} />
        </label>
      </div>
    </Modal>
  )
}
